import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Card, CardBody, Button, Input, Typography } from "@material-tailwind/react";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { selectClsLg } from "../constants/formClasses";
import PortalSelect from "../components/PortalSelect";
import { MEXICAN_STATES } from "../data/mexicanStates";
import { PortalShell } from "../components/PortalShell";

function userToForm(user) {
  return {
    state: user?.state || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    currentPassword: "",
    newPassword: "",
    newPasswordConfirm: "",
  };
}

export default function PortalClientProfile() {
  const { user, isAuthenticated, refreshMe } = usePortalAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => userToForm(user));
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== "client") {
      setBootLoading(false);
      return;
    }
    let cancelled = false;
    refreshMe()
      .then((u) => {
        if (cancelled || !u) return;
        setForm(userToForm(u));
      })
      .catch(() => toast.error("No se pudo cargar tu perfil"))
      .finally(() => {
        if (!cancelled) setBootLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.role, refreshMe]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role === "admin") {
    return <Navigate to="/admin" replace />;
  }
  if (user?.role === "psychologist") {
    return <Navigate to="/psicologo/perfil" replace />;
  }
  if (user?.role !== "client") {
    return <Navigate to="/login" replace />;
  }

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error("Nombre y apellidos son obligatorios");
      return;
    }
    if (!form.state) {
      toast.error("Selecciona tu estado");
      return;
    }
    const wantsPasswordChange = Boolean(
      form.currentPassword || form.newPassword || form.newPasswordConfirm
    );
    if (wantsPasswordChange) {
      if (!form.currentPassword) {
        toast.error("Indica tu contraseña actual");
        return;
      }
      if (form.newPassword.length < 8) {
        toast.error("La nueva contraseña debe tener al menos 8 caracteres");
        return;
      }
      if (form.newPassword !== form.newPasswordConfirm) {
        toast.error("Las contraseñas nuevas no coinciden");
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        state: form.state,
      };
      if (wantsPasswordChange) {
        payload.currentPassword = form.currentPassword;
        payload.newPassword = form.newPassword;
        payload.newPasswordConfirm = form.newPasswordConfirm;
      }

      const res = await portalApi.clientProfilePut(payload);
      if (res?.user) {
        localStorage.setItem("portal_user", JSON.stringify(res.user));
      }
      await refreshMe();

      setForm((f) => ({
        ...f,
        currentPassword: "",
        newPassword: "",
        newPasswordConfirm: "",
      }));
      toast.success("Perfil actualizado");
      navigate("/panel", { replace: true });
    } catch (err) {
      toast.error(err.message || "No se pudo guardar el perfil");
    } finally {
      setLoading(false);
    }
  }

  return (
    <PortalShell
      title="Mi perfil"
      subtitle="Actualiza tus datos de contacto. El correo de acceso no se puede cambiar desde aquí."
    >
      {bootLoading ? (
        <p className="text-brand-gray text-sm py-8 text-center">Cargando perfil…</p>
      ) : (
        <Card className="border border-brand-brown/15 shadow-lg shadow-brand-brown/5">
          <CardBody className="p-6 sm:p-8">
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-brand-dark mb-1.5">
                  Estado (México)
                </label>
                <PortalSelect size="lg"
                  className={selectClsLg}
                  value={form.state}
                  onChange={(e) => setField("state", e.target.value)}
                  required
                >
                  <option value="">Selecciona tu estado</option>
                  {MEXICAN_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </PortalSelect>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Nombre(s)"
                  value={form.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                  required
                  size="lg"
                  className="!text-brand-dark"
                />
                <Input
                  label="Apellidos"
                  value={form.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                  required
                  size="lg"
                  className="!text-brand-dark"
                />
              </div>
              <Input
                type="email"
                label="Correo electrónico"
                value={user.email || ""}
                readOnly
                disabled
                size="lg"
                className="!text-brand-dark opacity-80"
              />
              <p className="text-xs text-brand-gray -mt-2">
                Para cambiar el correo, contacta al equipo de EAP Consulting.
              </p>
              <Input
                label="Teléfono"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                autoComplete="tel"
                required
                size="lg"
                className="!text-brand-dark"
              />
              <div className="rounded-xl border border-brand-brown/15 bg-slate-50/60 p-4 sm:p-5 space-y-4">
                <Typography className="text-sm font-medium text-brand-dark">
                  Cambiar contraseña (opcional)
                </Typography>
                <Input
                  type="password"
                  label="Contraseña actual"
                  value={form.currentPassword}
                  onChange={(e) => setField("currentPassword", e.target.value)}
                  autoComplete="current-password"
                  size="lg"
                  className="!text-brand-dark"
                />
                <Input
                  type="password"
                  label="Nueva contraseña (mín. 8 caracteres)"
                  value={form.newPassword}
                  onChange={(e) => setField("newPassword", e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  size="lg"
                  className="!text-brand-dark"
                />
                <Input
                  type="password"
                  label="Confirmar nueva contraseña"
                  value={form.newPasswordConfirm}
                  onChange={(e) => setField("newPasswordConfirm", e.target.value)}
                  autoComplete="new-password"
                  size="lg"
                  className="!text-brand-dark"
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 pt-2">
                <Link
                  to="/panel"
                  className="text-center sm:text-left text-sm text-brand-gray hover:text-brand-dark py-2"
                >
                  Cancelar
                </Link>
                <Button
                  type="submit"
                  size="lg"
                  className="bg-brand-lavender w-full sm:w-auto sm:ml-auto normal-case text-base shadow-md shadow-brand-lavender/25"
                  disabled={loading}
                >
                  {loading ? "Guardando…" : "Guardar cambios"}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}
    </PortalShell>
  );
}
